import { pieceOf } from "@eldrun/content";
import { edgeAxis, GRID, isEdgeKind, pieceBoxes, terrainUnder, type GridPos, type PieceKind } from "@eldrun/sim";
import { WORLD_HALF } from "@eldrun/worldgen";
import { Box2, DataTexture, RedFormat, UnsignedByteType, Vector2, type WebGLRenderer } from "three";
import { structureFootprint } from "./structure-meshes.ts";

/**
 * Folga (m) entre a base da peça e o chão abaixo da qual o capim (até 0,75 m) atravessaria a peça:
 * ali ele some. Pisos em palafitas baixas apagam; pisos altos, andares de cima e telhados, não.
 */
export const GRASS_TALL = 0.8;
/** Faixa sem capim de cada lado de uma parede, porta ou estacada (m, da linha da aresta). */
export const EDGE_CLEAR = 0.25;
/** Um texel por célula de construção de 2 m: 1024² cobrem o mundo. */
export const MASK_SIDE = (2 * WORLD_HALF) / GRID.cell;
/** A célula (0, 0) da grade fica no texel (MASK_HALF, MASK_HALF). */
export const MASK_HALF = MASK_SIDE / 2;
/** Batente da porta (m, em cada ponta da aresta): o vão do meio mantém o capim. */
export const DOOR_JAMB = (GRID.cell - GRID.doorWidth) / 2;
/** Estações no shader, as mais perto da câmera. */
export const MAX_STATIONS = 32;
/** Só as estações a até 70 m da câmera vão ao shader (o capim acaba a 60 m dela, `GRASS_REACH.end`). */
export const STATION_RANGE = 70;

/**
 * Bits do texel da célula (gx, gz). `cell`: o interior (alicerce, piso, rampa); `edgeX`/`doorX`: a
 * aresta em x no z mínimo da célula (giros 0 e 2); `edgeZ`/`doorZ`: a aresta em z no x mínimo (giros 1
 * e 3). Parede e estacada limpam a aresta inteira; a porta, só os batentes.
 */
export const MASK_BIT = { cell: 1, edgeX: 2, edgeZ: 4, doorX: 8, doorZ: 16 } as const;

/** O que a máscara precisa de uma `Structure` do estado (peça da grade ou estação). */
export interface GrassStructure {
  itemId: string;
  grid: boolean;
  gx: number;
  gy: number;
  gz: number;
  rot: number;
  x: number;
  y: number;
  z: number;
}

type Height = (x: number, z: number) => number;

interface Station {
  x: number;
  z: number;
  /** Raio do contorno da malha (m). */
  r: number;
  /** Assentada a menos de `GRASS_TALL` do chão. */
  active: boolean;
  /** Distância à câmera ao quadrado, na última seleção. */
  d2: number;
}

const byDistance = (a: Station, b: Station) => a.d2 - b.d2;

/**
 * Bit que a peça liga na célula dela, ou 0 se o capim passa: telhado nunca apaga; as demais, só com
 * a base (a menor `minY` das caixas) a menos de `GRASS_TALL` acima do ponto mais alto do chão embaixo.
 */
export function grassBit(kind: PieceKind, p: GridPos, height: Height): number {
  if (kind === "telhado") return 0;
  let base = Infinity;
  for (const b of pieceBoxes(kind, p, true)) base = Math.min(base, b.minY);
  if (base - terrainUnder(kind, p, height).max >= GRASS_TALL) return 0;
  if (!isEdgeKind(kind)) return MASK_BIT.cell;
  const door = kind === "porta";
  if (edgeAxis(p.rot) === "x") return door ? MASK_BIT.doorX : MASK_BIT.edgeX;
  return door ? MASK_BIT.doorZ : MASK_BIT.edgeZ;
}

/** Aresta com o bit esconde o ponto a `along` m do começo dela (as pontas e os batentes da porta). */
const edgeHides = (bits: number, wall: number, door: number, along: number): boolean => (bits & wall) !== 0 || ((bits & door) !== 0 && (along < DOOR_JAMB || along > GRID.cell - DOOR_JAMB));

const f = (v: number) => (Number.isInteger(v) ? v.toFixed(1) : String(v));

/**
 * Trecho do vertex shader do capim (F53): `grassBuilt(xz)` diz se a lâmina nasce sob uma construção
 * do jogador. Lê o texel da própria célula e, perto de uma linha da grade, os das arestas (com as
 * pontas: a faixa passa `EDGE_CLEAR` das pontas da parede); depois as estações. O espelho na CPU é
 * `GrassMask.hiddenAt`.
 */
export const GRASS_MASK_GLSL = /* glsl */ `
uniform sampler2D uGrassMask;
uniform vec3 uStations[${MAX_STATIONS}];
uniform int uStationCount;

int grassMaskBits(int gx, int gz) {
  ivec2 c = ivec2(gx, gz) + ${MASK_HALF};
  ivec2 size = textureSize(uGrassMask, 0);
  if (c.x < 0 || c.y < 0 || c.x >= size.x || c.y >= size.y) return 0;
  return int(texelFetch(uGrassMask, c, 0).r * 255.0 + 0.5);
}

bool grassEdgeHides(int bits, int wall, int door, float along) {
  return (bits & wall) != 0 || ((bits & door) != 0 && (along < ${f(DOOR_JAMB)} || along > ${f(GRID.cell - DOOR_JAMB)}));
}

bool grassBuilt(vec2 p) {
  vec2 g = p / ${f(GRID.cell)};
  if ((grassMaskBits(int(floor(g.x)), int(floor(g.y))) & ${MASK_BIT.cell}) != 0) return true;
  float k = floor(g.y + 0.5);
  if (abs(p.y - k * ${f(GRID.cell)}) < ${f(EDGE_CLEAR)}) {
    int i0 = int(floor((p.x - ${f(EDGE_CLEAR)}) / ${f(GRID.cell)}));
    int i1 = int(floor((p.x + ${f(EDGE_CLEAR)}) / ${f(GRID.cell)}));
    if (grassEdgeHides(grassMaskBits(i0, int(k)), ${MASK_BIT.edgeX}, ${MASK_BIT.doorX}, p.x - float(i0) * ${f(GRID.cell)})) return true;
    if (i1 != i0 && grassEdgeHides(grassMaskBits(i1, int(k)), ${MASK_BIT.edgeX}, ${MASK_BIT.doorX}, p.x - float(i1) * ${f(GRID.cell)})) return true;
  }
  float m = floor(g.x + 0.5);
  if (abs(p.x - m * ${f(GRID.cell)}) < ${f(EDGE_CLEAR)}) {
    int j0 = int(floor((p.y - ${f(EDGE_CLEAR)}) / ${f(GRID.cell)}));
    int j1 = int(floor((p.y + ${f(EDGE_CLEAR)}) / ${f(GRID.cell)}));
    if (grassEdgeHides(grassMaskBits(int(m), j0), ${MASK_BIT.edgeZ}, ${MASK_BIT.doorZ}, p.y - float(j0) * ${f(GRID.cell)})) return true;
    if (j1 != j0 && grassEdgeHides(grassMaskBits(int(m), j1), ${MASK_BIT.edgeZ}, ${MASK_BIT.doorZ}, p.y - float(j1) * ${f(GRID.cell)})) return true;
  }
  for (int s = 0; s < ${MAX_STATIONS}; s++) {
    if (s >= uStationCount) break;
    vec3 st = uStations[s];
    vec2 d = p - st.xy;
    if (dot(d, d) < st.z * st.z) return true;
  }
  return false;
}
`;

/**
 * Máscara do capim sob as construções do jogador (F53). A densidade do capim é de 8 m (o plano) e não
 * representa células de 2 m, então a máscara é outra textura: `R8` de 1024², um texel por célula de
 * construção, com os bits de `MASK_BIT` contados por referência (peças sobrepostas). Estações vão num
 * `uniform vec3[32]` (x, z, raio do contorno da malha), as mais perto da câmera.
 *
 * Sem alocação por quadro: `update` só refaz a lista ao trocar de célula de 2 m ou mudar uma estação, e
 * `flush` sobe só o retângulo sujo por `copyTextureToTexture` a partir de uma textura de estágio que
 * divide o mesmo array e nunca é enviada (o three cai no `texSubImage2D` com `UNPACK_SKIP_*`; o
 * `addUpdateRange` dele supõe RGBA).
 */
export class GrassMask {
  readonly texture: DataTexture;
  readonly uniforms: {
    uGrassMask: { value: DataTexture };
    uStations: { value: Float32Array };
    uStationCount: { value: number };
  };
  private readonly data = new Uint8Array(MASK_SIDE * MASK_SIDE);
  private readonly staging: DataTexture;
  private readonly height: Height;
  /** Referências por (texel, bit): chave `texel × 32 + bit`. */
  private readonly counts = new Map<number, number>();
  /** Peças da grade por id: a chave do bit que ligaram, ou −1 (não apagam o capim). */
  private readonly pieces = new Map<string, number>();
  private readonly stations = new Map<string, Station>();
  private readonly order: Station[] = [];
  private readonly region = new Box2();
  private readonly at = new Vector2();
  /** Retângulo sujo em texels (inclusivo); vazio com `minX > maxX`. */
  private minX = MASK_SIDE;
  private minZ = MASK_SIDE;
  private maxX = -1;
  private maxZ = -1;
  private cellX = Number.NaN;
  private cellZ = Number.NaN;
  private stationsDirty = true;
  private uploads = 0;
  /** Último retângulo enviado: x, z, largura e altura em texels. */
  private readonly lastRect = [0, 0, 0, 0];

  /** `height`: o terreno da simulação (o mesmo `sampleHeight` do `HeightfieldWorld` e do servidor). */
  constructor(height: Height) {
    this.height = height;
    this.texture = new DataTexture(this.data, MASK_SIDE, MASK_SIDE, RedFormat, UnsignedByteType);
    this.texture.unpackAlignment = 1;
    this.texture.needsUpdate = true;
    // Só a origem da cópia: o three a lê da memória (nunca a envia).
    this.staging = new DataTexture(this.data, MASK_SIDE, MASK_SIDE, RedFormat, UnsignedByteType);
    this.uniforms = {
      uGrassMask: { value: this.texture },
      uStations: { value: new Float32Array(MAX_STATIONS * 3) },
      uStationCount: { value: 0 },
    };
  }

  /** Construção do jogador (peça da grade ou estação) entra na máscara; chamar de novo troca. */
  setStructure(id: string, s: GrassStructure): void {
    this.removeStructure(id);
    if (s.grid) {
      const spec = pieceOf(s.itemId);
      const bit = spec ? grassBit(spec.kind as PieceKind, s, this.height) : 0;
      const i = s.gx + MASK_HALF;
      const j = s.gz + MASK_HALF;
      const inside = i >= 0 && j >= 0 && i < MASK_SIDE && j < MASK_SIDE;
      const key = bit && inside ? (j * MASK_SIDE + i) * 32 + bit : -1;
      this.pieces.set(id, key);
      if (key >= 0) this.retain(key);
      return;
    }
    const station: Station = { x: s.x, z: s.z, r: structureFootprint(s.itemId), active: false, d2: 0 };
    this.stations.set(id, station);
    this.placeStation(station, s.y);
  }

  /** A estação mudou de altura (o piso embaixo dela saiu): confere de novo se está no chão. */
  setStationY(id: string, y: number): void {
    const station = this.stations.get(id);
    if (station) this.placeStation(station, y);
  }

  removeStructure(id: string): void {
    const key = this.pieces.get(id);
    if (key !== undefined) {
      this.pieces.delete(id);
      if (key >= 0) this.release(key);
    }
    const station = this.stations.get(id);
    if (station) {
      this.stations.delete(id);
      if (station.active) this.stationsDirty = true;
    }
  }

  /**
   * Refaz a lista de estações do shader se a câmera trocou de célula de 2 m, uma estação mudou ou uma
   * célula foi limpa ou solta. Ficam de fora as estações cujo disco cai inteiro em células já limpas
   * (dentro de casa, sobre o piso): não gastam as 32 vagas.
   */
  update(cameraX: number, cameraZ: number): void {
    const cx = Math.floor(cameraX / GRID.cell);
    const cz = Math.floor(cameraZ / GRID.cell);
    if (!this.stationsDirty && cx === this.cellX && cz === this.cellZ) return;
    this.cellX = cx;
    this.cellZ = cz;
    this.stationsDirty = false;
    this.order.length = 0;
    for (const s of this.stations.values()) {
      if (!s.active || this.insideCleared(s)) continue;
      s.d2 = (s.x - cameraX) ** 2 + (s.z - cameraZ) ** 2;
      if (s.d2 <= STATION_RANGE ** 2) this.order.push(s);
    }
    this.order.sort(byDistance);
    const n = Math.min(MAX_STATIONS, this.order.length);
    const out = this.uniforms.uStations.value;
    for (let k = 0; k < n; k++) {
      const s = this.order[k] as Station;
      out[k * 3] = s.x;
      out[k * 3 + 1] = s.z;
      out[k * 3 + 2] = s.r;
    }
    this.uniforms.uStationCount.value = n;
  }

  /** Sobe o retângulo sujo (nada se nada mudou). */
  flush(renderer: Pick<WebGLRenderer, "copyTextureToTexture">): void {
    if (this.minX > this.maxX) return;
    this.region.min.set(this.minX, this.minZ);
    this.region.max.set(this.maxX + 1, this.maxZ + 1);
    this.at.set(this.minX, this.minZ);
    renderer.copyTextureToTexture(this.staging, this.texture, this.region, this.at);
    this.lastRect[0] = this.minX;
    this.lastRect[1] = this.minZ;
    this.lastRect[2] = this.maxX - this.minX + 1;
    this.lastRect[3] = this.maxZ - this.minZ + 1;
    this.uploads++;
    this.minX = this.minZ = MASK_SIDE;
    this.maxX = this.maxZ = -1;
  }

  /** Espelho do `grassBuilt` do shader: a lâmina nascida em (x, z) some? */
  hiddenAt(x: number, z: number): boolean {
    const c = GRID.cell;
    if (this.bits(Math.floor(x / c), Math.floor(z / c)) & MASK_BIT.cell) return true;
    const k = Math.floor(z / c + 0.5);
    if (Math.abs(z - k * c) < EDGE_CLEAR) {
      const i0 = Math.floor((x - EDGE_CLEAR) / c);
      const i1 = Math.floor((x + EDGE_CLEAR) / c);
      if (edgeHides(this.bits(i0, k), MASK_BIT.edgeX, MASK_BIT.doorX, x - i0 * c)) return true;
      if (i1 !== i0 && edgeHides(this.bits(i1, k), MASK_BIT.edgeX, MASK_BIT.doorX, x - i1 * c)) return true;
    }
    const m = Math.floor(x / c + 0.5);
    if (Math.abs(x - m * c) < EDGE_CLEAR) {
      const j0 = Math.floor((z - EDGE_CLEAR) / c);
      const j1 = Math.floor((z + EDGE_CLEAR) / c);
      if (edgeHides(this.bits(m, j0), MASK_BIT.edgeZ, MASK_BIT.doorZ, z - j0 * c)) return true;
      if (j1 !== j0 && edgeHides(this.bits(m, j1), MASK_BIT.edgeZ, MASK_BIT.doorZ, z - j1 * c)) return true;
    }
    const s = this.uniforms.uStations.value;
    for (let n = 0; n < this.uniforms.uStationCount.value; n++) {
      if ((x - (s[n * 3] as number)) ** 2 + (z - (s[n * 3 + 1] as number)) ** 2 < (s[n * 3 + 2] as number) ** 2) return true;
    }
    return false;
  }

  /** Bits da célula (gx, gz) da grade; 0 fora do mundo. */
  bits(gx: number, gz: number): number {
    const i = gx + MASK_HALF;
    const j = gz + MASK_HALF;
    return i >= 0 && j >= 0 && i < MASK_SIDE && j < MASK_SIDE ? (this.data[j * MASK_SIDE + i] as number) : 0;
  }

  /**
   * Peças registradas (todas as da grade, apaguem ou não), estações (e quantas no chão e no shader),
   * envios, o último retângulo, os centros das células limpas e os discos das estações no shader.
   */
  stats(): { pieces: number; stations: number; grounded: number; shader: number; uploads: number; rect: number[]; cells: { x: number; z: number }[]; discs: { x: number; z: number; r: number }[] } {
    const out = this.uniforms.uStations.value;
    const discs = Array.from({ length: this.uniforms.uStationCount.value }, (_, k) => ({ x: out[k * 3] as number, z: out[k * 3 + 1] as number, r: out[k * 3 + 2] as number }));
    const cells: { x: number; z: number }[] = [];
    let grounded = 0;
    for (const s of this.stations.values()) if (s.active) grounded++;
    for (const key of this.counts.keys()) {
      if (key % 32 !== MASK_BIT.cell) continue;
      const texel = Math.floor(key / 32);
      cells.push({ x: ((texel % MASK_SIDE) - MASK_HALF + 0.5) * GRID.cell, z: (Math.floor(texel / MASK_SIDE) - MASK_HALF + 0.5) * GRID.cell });
    }
    return { pieces: this.pieces.size, stations: this.stations.size, grounded, shader: this.uniforms.uStationCount.value, uploads: this.uploads, rect: [...this.lastRect], cells, discs };
  }

  dispose(): void {
    this.texture.dispose();
    this.staging.dispose();
  }

  /** O disco da estação cai inteiro em células com o interior limpo (o quadrado em volta dele basta). */
  private insideCleared(s: Station): boolean {
    const c = GRID.cell;
    for (let gz = Math.floor((s.z - s.r) / c); gz <= Math.floor((s.z + s.r) / c); gz++) {
      for (let gx = Math.floor((s.x - s.r) / c); gx <= Math.floor((s.x + s.r) / c); gx++) if (!(this.bits(gx, gz) & MASK_BIT.cell)) return false;
    }
    return true;
  }

  private placeStation(station: Station, y: number): void {
    const active = y - this.height(station.x, station.z) < GRASS_TALL;
    if (active === station.active) return;
    station.active = active;
    this.stationsDirty = true;
  }

  private retain(key: number): void {
    const n = (this.counts.get(key) ?? 0) + 1;
    this.counts.set(key, n);
    if (n === 1) this.write(key, true);
  }

  private release(key: number): void {
    const n = (this.counts.get(key) ?? 1) - 1;
    if (n > 0) {
      this.counts.set(key, n);
      return;
    }
    this.counts.delete(key);
    this.write(key, false);
  }

  private write(key: number, on: boolean): void {
    const texel = Math.floor(key / 32);
    const bit = key % 32;
    this.data[texel] = on ? (this.data[texel] as number) | bit : (this.data[texel] as number) & ~bit;
    // Uma estação dentro desta célula pode entrar ou sair da lista do shader.
    if (bit === MASK_BIT.cell && this.stations.size > 0) this.stationsDirty = true;
    const i = texel % MASK_SIDE;
    const j = Math.floor(texel / MASK_SIDE);
    this.minX = Math.min(this.minX, i);
    this.maxX = Math.max(this.maxX, i);
    this.minZ = Math.min(this.minZ, j);
    this.maxZ = Math.max(this.maxZ, j);
  }
}
